import React, { useEffect, useMemo, useRef, useState } from "react";
import { Offcanvas, Form, Button, Tab, Tabs } from "react-bootstrap";
import Select from "react-select";

function AddZoneOffCanvasWizard(props) {
  const {
    show,
    handleClose,
    item = null,
    plantId,
    parentOptions = [],
    deviceOptions = [],
    onSave = async () => {},
  } = props;

  const [zoneName, setZoneName] = useState(item?.name || "");
  const [zoneDesc, setZoneDesc] = useState(item?.description || "");
  const [parentOpt, setParentOpt] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const initialDeviceIdsRef = useRef(new Set());

  // NEW: control tabs
  const [activeTab, setActiveTab] = useState("basic_details");
  const basicFormRef = useRef(null);

  const saveLabel = item ? "Edit & Save" : "Save & Add";

  useEffect(() => {
    setZoneName(item?.name || "");
    setZoneDesc(item?.description || "");

    const currentParentId = item?.parent?.id ?? item?.parent_id ?? 1;
    const found =
      parentOptions.find((o) => Number(o.value) === Number(currentParentId)) ||
      parentOptions.find((o) => Number(o.value) === 1) ||
      null;

    setParentOpt(found);
    setActiveTab("basic_details");
  }, [item, parentOptions]);

  useEffect(() => {
    if (!item?.installed_devices) {
      setSelectedDevices([]);
      return;
    }
    const byId = new Map(
      (deviceOptions || []).map((o) => [String(o.value), o])
    );
    initialDeviceIdsRef.current = new Set(
      item.installed_devices.map((d) => String(d.id))
    );
    const prefilled = item.installed_devices.map((d) => {
      const hit = byId.get(String(d.id));
      return hit || { value: d.id, label: d.name || d.id };
    });
    setSelectedDevices(prefilled);
  }, [item, deviceOptions, show]);

  const submit = async (e) => {
    e.preventDefault();
    if (!zoneName?.trim() || !plantId) return;

    const payload = {
      id: item?.id,
      name: zoneName.trim(),
      description: zoneDesc || "",
      plant_id: Number(plantId),
      parent_id: parentOpt?.value != null ? Number(parentOpt.value) : 1,
      is_active: true,
      selected_device_ids: selectedDevices
        .map((d) => Number(d?.value))
        .filter(Number.isFinite),
    };

    try {
      setSaving(true);
      await onSave(payload);
      handleClose(true);
    } catch (err) {
      console.error("Failed to save zone:", err);
    } finally {
      setSaving(false);
    }
  };

  const goNextToDevices = () => {
    if (basicFormRef.current && !basicFormRef.current.checkValidity()) {
      basicFormRef.current.reportValidity();
      return;
    }
    setActiveTab("devices");
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => handleClose()}
      placement="end"
      backdrop="static"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{item ? "Edit a Zone" : "Add a Zone"}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="d-flex flex-column">
        <Tabs
          id="add_zone"
          className="border-top py-1"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "basic_details")}
        >
          <Tab
            eventKey="basic_details"
            className="h-100"
            title={
              <div className="d-flex align-items-center">
                <i className="fa fa-bullseye mr-1"></i>
                <span>Basic Details</span>
              </div>
            }
          >
            <Form
              ref={basicFormRef}
              className="mt-4 d-flex flex-column flex-grow-1 h-100"
              onSubmit={(e) => e.preventDefault()}
            >
              <Form.Group className="mb-3">
                <Form.Label>
                  Parent Zone <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  inputId="parent_zones"
                  options={parentOptions}
                  value={parentOpt}
                  onChange={(opt) => setParentOpt(opt)}
                  placeholder="Select a zone (default value = id:1)"
                  isClearable
                  isDisabled={saving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter here"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  required
                  disabled={saving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Max allowed characters: 120"
                  value={zoneDesc}
                  onChange={(e) => setZoneDesc(e.target.value)}
                  disabled={saving}
                />
              </Form.Group>

              <div className="d-flex justify-content-end border-top pt-2 mt-auto mb-3">
                <Button
                  variant="outline-danger"
                  onClick={() => handleClose()}
                  disabled={saving}
                >
                  Discard
                </Button>

                <Button
                  className="ms-2"
                  variant="primary"
                  type="button"
                  onClick={goNextToDevices}
                  disabled={saving}
                >
                  Next
                </Button>
              </div>
            </Form>
          </Tab>

          <Tab
            eventKey="devices"
            className="h-100"
            title={
              <div className="d-flex align-items-center">
                <i className="fa fa-bolt mr-1"></i>
                <span>Devices</span>
              </div>
            }
          >
            <Form
              className="mt-4 d-flex flex-column flex-grow-1 h-100"
              onSubmit={submit}
            >
              <Form.Group className="mb-3">
                <Form.Label>
                  Devices <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  options={deviceOptions}
                  placeholder="Select devices"
                  isMulti
                  value={selectedDevices}
                  onChange={(val) => setSelectedDevices(val || [])}
                  isDisabled={saving}
                  components={{ MultiValue: () => null }}
                  closeMenuOnSelect={true}
                  hideSelectedOptions={true}
                />
              </Form.Group>

              {selectedDevices.length > 0 && (
                <>
                  <div className="mb-2 fw-semibold">Linked Devices</div>
                  <div className="d-flex flex-column" style={{ gap: 10 }}>
                    {selectedDevices.map((d) => {
                      const isNew = !initialDeviceIdsRef.current.has(
                        String(d.value)
                      );
                      return (
                        <div
                          key={d.value}
                          className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: 10 }}
                          >
                            <span className="badge bg-light text-dark">
                              <i className="fa fa-bolt" aria-hidden="true" />
                            </span>

                            {isNew && (
                              <span className="badge bg-success text-uppercase">
                                NEW
                              </span>
                            )}

                            <span className="text-truncate">{d.label}</span>
                          </div>

                          <button
                            type="button"
                            className="btn btn-link text-danger p-0"
                            onClick={() =>
                              setSelectedDevices((prev) =>
                                prev.filter((x) => x.value !== d.value)
                              )
                            }
                            title="Unlink"
                            aria-label={`Unlink ${d.label}`}
                          >
                            <i className="fa fa-unlink" aria-hidden="true" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="d-flex justify-content-end border-top pt-2 mt-auto sticky-bottom bg-white pb-3">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => handleClose()}
                  disabled={saving}
                >
                  Discard
                </Button>

                <Button
                  className="ms-2"
                  variant="primary"
                  type="submit"
                  disabled={saving}
                >
                  {saving && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  {saving ? "Saving…" : saveLabel}
                </Button>
              </div>
            </Form>
          </Tab>
        </Tabs>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default AddZoneOffCanvasWizard;
